import React, { useState, useCallback, useMemo } from 'react';
import { PostConsignment, handleDeleteConsignment } from '../../../api/GBENSDec';

const useConsignment = (
  currentConsignment,
  setCurrentConsignment,
  currentConsignmentErrors,
  setCurrentConsignmentErrors,
  currentConsignmentHMRCErrors,
  setCurrentConsignmentHMRCErrors,
  data,
  setData,
  errors,
  setErrors,
  errorsHMRC,
  setErrorsHMRC,
  headerId,
  refreshNeenors,
  referenceData
) => {
  const [currentLine, setCurrentLine] = useState(null);

  const getNewLineID = useCallback(() => {
    if (!currentConsignment || !Array.isArray(currentConsignment.lines)) {
      console.error('currentConsignment or lines is null/undefined:', currentConsignment);
      return -100000;
    }
    return currentConsignment.lines.reduce((p, c) => {
      if (c.id >= 0) return p;
      return Math.max(p, c.id + 1);
    }, -100000);
  }, [currentConsignment]);

  const hasConsignments = useMemo(() => data.Consignments?.length > 0, [data]);

  const addNewConsignment = useCallback(() => {
    const c = { lines: [], Consignee: {}, Consignor: {}, isEditable: true };
    setCurrentConsignment(c);
    const e = { lines: [] };
    setCurrentConsignmentErrors(e);
    const e2 = { lines: [] };
    setCurrentConsignmentHMRCErrors(e2);
  }, [data, errors]);

  const editConsignment = useCallback(
    (id) => {
      const c = data.Consignments.find((c) => c.id === id);
      if (!c.Consignor) c.Consignor = {};
      if (!c.Consignee) c.Consignee = {};
      setCurrentConsignment(c);
      const e = errors.Consignments?.find?.((e) => e.id === id);
      setCurrentConsignmentErrors(e);
      const e2 = errorsHMRC.Consignments?.find?.((e) => e.id === id);
      setCurrentConsignmentHMRCErrors(e2);
    },
    [data, errors]
  );

  const updateCurrentConsignment = useCallback(
    (e, field, modal) => {
      const value = e.target.value;
      if (!modal)
        setCurrentConsignment((prev) => ({
          ...prev,
          [field]: value,
        }));
      else if (currentConsignment[modal])
        setCurrentConsignment((prev) => ({
          ...prev,
          [modal]: {
            ...prev[modal],
            [field]: value,
          },
        }));
      else
        setCurrentConsignment((prev) => ({
          ...prev,
          [modal]: {
            [field]: value,
          },
        }));
    },
    [currentConsignment]
  );

  const saveCurrentConsignment = async () => {
    if (!currentConsignment.isEditable) {
      return;
    }

    //TODO: temporary solution to format currentConsignment data before saving
    const formattedCurrentConsignment = {
      ...currentConsignment,
      PlaceOfAcceptance: {
        country: currentConsignment?.placeOfAcceptanceCountry || '',
        placeName: currentConsignment?.placeOfAcceptancePlaceName || '',
      },
      PlaceOfDelivery: {
        country: currentConsignment?.placeOfDeliveryCountry || '',
        placeName: currentConsignment?.placeOfDeliveryPlaceName || '',
      },
    };

    // loop through formattedCurrentConsignment lines and make sure dangerousGoods is 4 characters or less
    // also remove any non-numeric characters just in case, and ensure it's a string
    formattedCurrentConsignment.lines = formattedCurrentConsignment.lines.map((line) => {
      return {
        ...line,
        dangerousGoods: line.dangerousGoods?.toString().replace(/\D/g, '').slice(-4) || '',
      };
    });

    const dt = await PostConsignment({ headerId, Consignments: [formattedCurrentConsignment] });
    await refreshNeenors();

    let id;
    let itemIds = [];
    if (dt?.[0]?.consignmentId) {
      id = dt[0].consignmentId;
      if (dt[0].insertedConsignmentItemIds) {
        itemIds = JSON.parse(dt[0].insertedConsignmentItemIds);
      }
    } else {
      throw new Error('Error saving consignment');
    }

    const idMap = currentConsignment.lines.map((l, i) => ({ prev: l.id, new: itemIds[i].id }));

    // if existing consignment then use existing isEditable value, otherwise default to true
    const isEditable = typeof currentConsignment.isEditable !== 'undefined' ? currentConsignment.isEditable : true;

    // calculate consignment number based on the highest number from other consignments
    const newConsignmentNumber = data?.Consignments?.length
      ? Math.max(...data.Consignments.map((obj) => obj.consignmentNumber)) + 1
      : 1;

    // changes for new or existing consignment stored here
    const newConsignment = {
      consignmentNumber: newConsignmentNumber,
      ...currentConsignment,
      lines: currentConsignment.lines.map((l, i) => {
        return { ...l, id: itemIds[i].id };
      }),
      id,
      isEditable,
    };

    setData((prev) => ({
      ...prev,
      Consignments: [
        ...prev.Consignments.map((c) => {
          // if existing consignment, replace it with the new data
          return c.id !== currentConsignment.id ? c : newConsignment;
        }),
        // if new consignment, add the new data to the end of the list
        ...(prev.Consignments.some((c) => c.id === currentConsignment.id) ? [] : [newConsignment]),
      ],
    }));

    setErrors((prev) => {
      const filtered = prev.Consignments ? prev.Consignments.filter((c) => c.id !== currentConsignmentErrors?.id) : [];
      return {
        ...prev,
        Consignments: [
          ...filtered,
          {
            ...currentConsignmentErrors,
            lines: currentConsignmentErrors.lines.map((l, i) => {
              return { ...l, id: idMap.find((i) => i.prev === l.id).new };
            }),
            id,
          },
        ],
      };
    });
    setErrorsHMRC((prev) => {
      const filtered = prev.Consignments
        ? prev.Consignments.filter((c) => c.id !== currentConsignmentHMRCErrors.id)
        : [];
      return {
        ...prev,
        Consignments: [...filtered, { ...currentConsignmentHMRCErrors, id }],
      };
    });
  };

  const moveConsignmentData = useCallback(
    (modalFrom, modalTo, deleteModalFrom) => {
      if (!deleteModalFrom)
        setCurrentConsignment((prev) => ({
          ...prev,
          [modalTo]: prev?.[modalFrom] ? JSON.parse(JSON.stringify(prev[modalFrom])) : {},
        }));
      else
        setCurrentConsignment((prev) => ({
          ...prev,
          [modalTo]: prev?.[modalFrom] ? JSON.parse(JSON.stringify(prev[modalFrom])) : {},
          [modalFrom]: undefined,
        }));
    },
    [currentConsignment]
  );

  const setBlankConsignmentData = useCallback(
    (modal, newData) => {
      setCurrentConsignment((prev) => ({
        ...prev,
        [modal]: { ...newData, id: -1 },
      }));
    },
    [currentConsignment]
  );

  const selectNeenor = (modal, value) => {
    setCurrentConsignment((prev) => ({
      ...prev,
      [modal]: value,
    }));
  };

  const deleteConsignmentData = useCallback(
    (modal) => {
      setCurrentConsignment((prev) => ({
        ...prev,
        [modal]: undefined,
      }));
    },
    [currentConsignment]
  );

  const addNewConsignmentLine = useCallback(() => {
    const l = { id: getNewLineID(), packagingMarks: 'As Addressed' };
    setCurrentLine(l);
  }, [currentLine, currentConsignment]);

  const updateCurrentLine = useCallback(
    (e, field) => {
      const value = e?.target?.value;
      setCurrentLine((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [currentLine]
  );

  const editConsignmentLine = useCallback(
    (id) => {
      setCurrentLine({ ...currentConsignment.lines.find((l) => l.id === id) });
    },
    [currentConsignment]
  );

  const saveConsignmentLine = useCallback(() => {
    setCurrentConsignment((prev) => {
      if (!prev) return { lines: [currentLine] };
      if (!Array.isArray(prev.lines)) return { ...prev, lines: [currentLine] };

      const newLines = [...(prev.lines || []).filter((p) => p.id !== currentLine.id), currentLine].sort((a, b) => {
        if (a.id < 0 && b.id > 0) return 1;
        else if (a.id > 0 && b.id < 0) return -1;
        else return a.id - b.id;
      });

      return {
        ...prev,
        lines: newLines,
      };
    });
  }, [currentLine]);

  const deleteConsignmentLine = useCallback(() => {
    setCurrentConsignment((prev) => ({
      ...prev,
      lines: [...prev.lines.filter((l) => l.id !== currentLine.id)],
    }));
    setCurrentLine(null);
  }, [currentLine]);

  const deleteConsignment = useCallback(
    async (ids) => {
      // Wrap a single id in an array
      const idsArray = Array.isArray(ids) ? ids : [ids];
      // Convert the selected IDs to numbers (in case they come as strings)
      const numericIds = idsArray.map((id) => Number(id));

      // Call the API with the array of numeric IDs
      const result = await handleDeleteConsignment(numericIds);
      if (result?.message !== 'Consignment deleted') {
        return;
      }

      // Update the data state by filtering out all consignments whose id is in numericIds
      setData((prev) => {
        const cons = prev.Consignments?.filter((consignment) => !numericIds.includes(consignment.id)); // remove deleted consignment
        return {
          ...prev,
          Consignments: cons?.map((con, idx) => ({ ...con, consignmentNumber: idx + 1 })), // re-number the remaining consignments
        };
      });

      // Similarly update errors (if errors.Consignments exists as an array)
      setErrors((prev) => ({
        ...prev,
        Consignments: prev.Consignments.filter((consignment) => !numericIds.includes(consignment.id)),
      }));

      setErrorsHMRC((prev) => ({
        ...prev,
        Consignments: prev.Consignments.filter((consignment) => !numericIds.includes(consignment.id)),
      }));

      setCurrentConsignment(null);
    },
    [data, errors]
  );

  const handleGetMRNDetails = (data) => {
    //if data is an empty array, set current consignment to null
    if (data.length === 0) {
      setCurrentConsignment({ lines: [], Consignee: {}, Consignor: {}, isEditable: true });
    } else {
      let idCount = -100000;
      const lines = [];
      //consignmentReference
      updateCurrentConsignment({ target: { value: data[0].consignmentReference } }, 'consignmentReference');
      const consignorData = {
        companyName: data[0]?.Consignor?.companyName || '',
        eori: data[0]?.Consignor?.EORI || '',
        streetNumber: data[0]?.Consignor?.streetNumber || '',
        streetAddress: data[0]?.Consignor?.streetAddress || '',
        townCity: data[0]?.Consignor?.townCity || '',
        postCode: data[0]?.Consignor?.postCode || '',
        Country: data[0]?.Consignor?.Country?.toUpperCase?.() || '',
      };

      const consigneeData = {
        companyName: data[0]?.Consignee?.companyName || '',
        eori: data[0]?.Consignee?.EORI || '',
        streetNumber: data[0]?.Consignee?.streetNumber || '',
        streetAddress: data[0]?.Consignee?.streetAddress || '',
        townCity: data[0]?.Consignee?.townCity || '',
        postCode: data[0]?.Consignee?.postCode || '',
        Country: data[0]?.Consignee?.Country?.toUpperCase?.() || '',
      };

      const placeOfLoading = {
        country: data[0]?.PlaceOfLoading?.country || '',
        placeName: data[0]?.PlaceOfLoading?.placeName || '',
        postcode: data[0]?.PlaceOfLoading?.postcode || '',
      };

      const placeOfUnloading = {
        country: data[0]?.PlaceOfUnloading?.country || '',
        placeName: data[0]?.PlaceOfUnloading?.placeName || '',
        postcode: data[0]?.PlaceOfUnloading?.postcode || '',
      };

      data[0].lines.forEach((line) => {
        const numPieces = line?.numPackages?.toString?.() || line?.numPieces?.toString?.() || '';
        const packagingMarks = line?.packagingMarks || 'As Addressed';
        const packageType = line?.packageType?.toString?.() || '241'; //default to 241 - package - if package type invalid or not provided

        const marksFieldDisabled = referenceData?.packagesWithPiecesDisabled?.includes(packageType?.toString());

        const currentLine = {
          id: idCount,
          goodsDescription: line?.goodsDescription || '',
          grossWeight: line?.grossWeight?.toString?.() || '',
          numPieces: marksFieldDisabled ? '' : numPieces,
          packageType: packageType,
          packagingMarks: marksFieldDisabled ? '' : packagingMarks,
          dangerousGoods: line?.dangerousGoods || '',
          commodityCode: line?.commcode || '',
        };
        lines.push(currentLine);
        idCount--;
      });

      setCurrentConsignment((prev) => ({
        ...prev,
        Consignor: {
          ...consignorData,
        },
        Consignee: {
          ...consigneeData,
        },
        PlaceOfLoading: {
          ...placeOfLoading,
        },
        PlaceOfUnloading: {
          ...placeOfUnloading,
        },
        lines: lines,
      }));
    }
  };

  return {
    addNewConsignment,
    editConsignment,
    updateCurrentConsignment,
    saveCurrentConsignment,
    moveConsignmentData,
    setBlankConsignmentData,
    selectNeenor,
    deleteConsignmentData,
    addNewConsignmentLine,
    updateCurrentLine,
    editConsignmentLine,
    saveConsignmentLine,
    deleteConsignmentLine,
    deleteConsignment,
    hasConsignments,
    currentLine,
    handleGetMRNDetails,
  };
};

export default useConsignment;
